/**
 * Detector de interacción activa del usuario en viewport.
 * - Mid-gesto: menos concurrency de decode + defer Redux/Explorer/IDB (CPU → scroll)
 * - Scheduler prioriza slice visible (P0)
 * - onInstanceReady pinta viewport sin esperar el store
 */

const DEFAULT_COOLDOWN_MS = 300
const DEFAULT_BURST_THRESHOLD = 5

/**
 * @typedef {{
 *   isInteracting: boolean,
 *   lastInteractionTime: number,
 *   interactionCount: number,
 *   interactionType: 'scroll'|'drag'|'zoom'|'pan'|'idle',
 * }} InteractionState
 */

/**
 * Singleton global para estado de interacción.
 */
let globalState = {
  isInteracting: false,
  lastInteractionTime: 0,
  interactionCount: 0,
  interactionType: 'idle',
}

const listeners = new Set()
let cooldownTimer = null

/**
 * @returns {InteractionState}
 */
export function getInteractionState() {
  return { ...globalState }
}

/**
 * @param {(state: InteractionState) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeInteraction(callback) {
  listeners.add(callback)
  return () => listeners.delete(callback)
}

function notifyListeners() {
  const snapshot = getInteractionState()
  for (const cb of listeners) {
    try {
      cb(snapshot)
    } catch (e) {
      console.warn('viewportInteractionDetector: listener error', e)
    }
  }
}

function updateState(patch) {
  const wasInteracting = globalState.isInteracting
  globalState = { ...globalState, ...patch }
  if (globalState.isInteracting !== wasInteracting) {
    notifyListeners()
  }
}

/**
 * Marca inicio de interacción.
 * @param {'scroll'|'drag'|'zoom'|'pan'} type
 */
export function markInteractionStart(type = 'scroll') {
  if (cooldownTimer) {
    clearTimeout(cooldownTimer)
    cooldownTimer = null
  }

  const now = performance.now()
  const timeSinceLast = now - globalState.lastInteractionTime
  const isBurst = timeSinceLast < 100

  updateState({
    isInteracting: true,
    lastInteractionTime: now,
    interactionCount: isBurst ? globalState.interactionCount + 1 : 1,
    interactionType: type,
  })
}

/**
 * Marca fin de interacción (con cooldown).
 * @param {number} [cooldownMs]
 */
export function markInteractionEnd(cooldownMs = DEFAULT_COOLDOWN_MS) {
  if (cooldownTimer) {
    clearTimeout(cooldownTimer)
  }

  cooldownTimer = setTimeout(() => {
    updateState({
      isInteracting: false,
      interactionType: 'idle',
      interactionCount: 0,
    })
    cooldownTimer = null
  }, cooldownMs)
}

/**
 * Resetea estado (útil para tests o unmount).
 */
export function resetInteractionState() {
  if (cooldownTimer) {
    clearTimeout(cooldownTimer)
    cooldownTimer = null
  }
  globalState = {
    isInteracting: false,
    lastInteractionTime: 0,
    interactionCount: 0,
    interactionType: 'idle',
  }
  notifyListeners()
}

/**
 * ¿Está el usuario en una interacción intensa? (burst de eventos)
 * @returns {boolean}
 */
export function isIntenseInteraction() {
  return (
    globalState.isInteracting &&
    globalState.interactionCount >= DEFAULT_BURST_THRESHOLD
  )
}

/**
 * Factor de throttle sugerido para descargas de fondo (0-1).
 * Mid-gesto: bajar un poco el parallelismo efectivo (CPU para paint/scroll).
 * @returns {number}
 */
export function getDownloadThrottleFactor() {
  if (isIntenseInteraction()) return 0.4
  if (globalState.isInteracting) return 0.65
  return 1.0
}

/**
 * Concurrency sugerida para descargas durante interacción.
 * Mid-scroll: menos decode/fetch en paralelo → más headroom de CPU para rAF.
 * @param {number} baseParallel - parallelismo base del scheduler
 * @returns {number}
 */
export function getAdjustedConcurrency(baseParallel) {
  const base = Math.max(1, Math.floor(Number(baseParallel) || 1))
  if (isIntenseInteraction()) return Math.max(1, Math.min(2, base))
  if (globalState.isInteracting) return Math.max(2, Math.min(3, Math.ceil(base * 0.5)))
  return base
}

/**
 * ¿Diferir flush Redux/Explorer (caro) mientras hay gesto?
 * Los pixels llegan al viewport vía onInstanceReady; el store se pone al día en idle.
 * @returns {boolean}
 */
export function shouldDeferHeavyUiFlush() {
  if (!globalState.isInteracting) return false
  // Evitar defer eterno si markInteractionEnd no corrió (UI/IDB “congelados”)
  const age = performance.now() - (globalState.lastInteractionTime || 0)
  return age < 2000
}

/**
 * ¿Debemos diferir escrituras IDB no críticas?
 * Durante gesto de viewport: priorizar scroll/paint; IDB retoma en idle.
 * (Las imágenes ya están en RAM vía onInstanceReady / decode.)
 * @returns {boolean}
 */
export function shouldDeferIdbWrites() {
  if (!globalState.isInteracting) return false
  const age = performance.now() - (globalState.lastInteractionTime || 0)
  return age < 2000
}

/**
 * Programa trabajo cuando el gesto terminó (idle del navegador).
 * @param {() => void} fn
 * @param {{ timeout?: number }} [opts]
 * @returns {number|ReturnType<typeof setTimeout>|null} handle
 */
export function scheduleWhenIdle(fn, opts = {}) {
  if (typeof fn !== 'function') return null
  const timeout = opts.timeout != null ? Number(opts.timeout) : 800
  try {
    if (typeof requestIdleCallback === 'function') {
      return requestIdleCallback(
        () => {
          try {
            fn()
          } catch (e) {
            /* ignore */
          }
        },
        { timeout: Math.max(100, timeout) }
      )
    }
  } catch (e) {
    /* ignore */
  }
  return setTimeout(() => {
    try {
      fn()
    } catch (e2) {
      /* ignore */
    }
  }, Math.min(200, Math.max(50, timeout / 4)))
}

/**
 * Cancela un handle de scheduleWhenIdle.
 * @param {*} handle
 */
export function cancelWhenIdle(handle) {
  if (handle == null) return
  try {
    if (typeof cancelIdleCallback === 'function') {
      cancelIdleCallback(handle)
    }
  } catch (e) {
    /* ignore */
  }
  try {
    clearTimeout(handle)
  } catch (e2) {
    /* ignore */
  }
}
